import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Order, getObjectId } from '@hero/schema';
import { PODRefCellModal } from './PODRefCellModal';

export interface PODRefCellProps {
  order: Order;
  onUpdate?: (order: Order) => void;
  updateOrderFn?: (data: { _id: string; pod: { podRef?: string } }) => Promise<Order>;
}

/**
 * POD Reference Cell Component
 * Displays the POD reference number with ability to edit in a modal
 */
export const PODRefCell: React.FC<PODRefCellProps> = ({ order, onUpdate, updateOrderFn }) => {
  const { t } = useTranslation('m-order');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSave = async (podRef: string) => {
    if (updateOrderFn) {
      try {
        const updatedOrder = await updateOrderFn({
          _id: order._id.toString(),
          pod: {
            podRef: podRef.trim() || undefined,
          },
        });
        if (onUpdate) {
          onUpdate(updatedOrder);
        }
      } catch (error) {
        console.error('Failed to update POD reference:', error);
        throw error;
      }
    } else if (onUpdate) {
      // Fallback if no update function provided
      const newOrder = {
        ...order,
        podRef: podRef.trim() || undefined,
      };
      onUpdate(newOrder as Order);
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <div
        onClick={e => {
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        className="cursor-pointer hover:text-primary transition-colors text-start">
        {order.podRef ? (
          <span className="text-sm text-gray-700">{order.podRef}</span>
        ) : (
          <span className="text-sm text-gray-400 italic">{t('table.columns.add_ref')}</span>
        )}
      </div>
      <PODRefCellModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        order={order}
        onSave={handleSave}
      />
    </>
  );
};
